﻿import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import Chart from 'react-apexcharts';

import Content_Container from '@Elements/Content_Container';

import { Customer_Day_Chart_Props } from '@Util/Types';

export default function Customer_Day_Chart(props: Customer_Day_Chart_Props): JSX.Element {
    const [t] = useTranslation();

    const chartData = useMemo(() => {
        if (!props.todays_time_log || props.todays_time_log.length === 0) {
            return [];
        }

        let minHour = Infinity;
        let maxHour = -Infinity;

        props.todays_time_log.forEach(log => {
            const startHour = dayjs(log.startDate).hour();
            const endHour = dayjs(log.endDate).hour();

            minHour = Math.min(minHour, startHour);
            maxHour = Math.max(maxHour, endHour);
        });

        // Ensure minHour and maxHour are valid
        if (minHour === Infinity || maxHour === -Infinity || minHour > maxHour) {
            return [];
        }

        const hoursData = Array(maxHour - minHour + 1).fill(null).map(() => ({}));
        const customers = new Set();

        props.todays_time_log.forEach(log => {
            const startHour = dayjs(log.startDate).hour() - minHour;
            const endHour = dayjs(log.endDate).hour() - minHour;

            for (let hour = startHour; hour <= endHour; hour++) {
                const startMinute = hour === startHour ? dayjs(log.startDate).minute() : 0;
                const endMinute = hour === endHour ? dayjs(log.endDate).minute() : 60;
                const minutes = endMinute - startMinute;

                if (!hoursData[hour][log.customerName]) {
                    hoursData[hour][log.customerName] = 0;
                }
                hoursData[hour][log.customerName] += minutes;
            }

            customers.add(log.customerName);
        });

        const series = Array.from(customers).map((customer: string) => {
            const logEntry = props.todays_time_log.find(log => log.customerName === customer);
            const color = logEntry && logEntry.color ? `rgb(${logEntry.color})` : 'rgb(0, 0, 0)'; // Default color is black
            return {
                name: customer,
                data: hoursData.map(hour => (hour as { [key: string]: number })[customer] || 0),
                color
            };
        });

        return series;
    }, [props.todays_time_log]);

    if (!chartData.length) {
        return null;
    }

    const minHour = Math.min(...props.todays_time_log.map(log => dayjs(log.startDate).hour()));

    return (
        <section className="Module">
            <header className="Module_Header">
                <h2 className="Headline2">{t('Text.Today')}</h2>
            </header>
            <Content_Container>
                {chartData.length ? (
                    <Chart
                        options={{
                            chart: {
                                type: 'bar',
                                stacked: true,
                                toolbar: {
                                    show: true
                                },
                                zoom: {
                                    enabled: true
                                }
                            },
                            dataLabels: {
                                enabled: false
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                },
                            },
                            tooltip: {
                                custom: function ({ seriesIndex, w }) {
                                    return (
                                        '<div style="padding: 5px 10px; background-color: #282c32; color: #ffffff;">' +
                                        '<span>' + w.globals.seriesNames[seriesIndex] + '</span>' +
                                        '</div>'
                                    );
                                }
                            },
                            xaxis: {
                                categories: Array.from({ length: chartData[0].data.length }, (_, i) => `${i + minHour}:00 - ${i + minHour + 1}:00`),
                                labels: {
                                    style: {
                                        colors: '#929292'
                                    }
                                }
                            },
                            yaxis: {
                                min: 0,
                                max: 60,
                                labels: {
                                    formatter: (value) => `${value} min`,
                                    style: {
                                        colors: '#929292'
                                    }
                                },
                            },
                            legend: {
                                position: 'bottom',
                                horizontalAlign: 'right',
                                labels: {
                                    colors: '#929292'
                                }
                            },
                            fill: {
                                opacity: 1,
                                colors: chartData.map(s => s.color),
                            },
                        }}
                        series={chartData}
                        type="bar"
                        height="350"
                    />
                ) : (
                        <Skeleton
                            width={'100%'}
                            height={350}
                            baseColor="#1c1e21"
                            highlightColor="#282c32"
                        />
                )}
            </Content_Container>
        </section>
    )
}